// Classic /schedule: the season's games that belong to no competition (#478),
// grouped by day, in the same card style as ScheduleRoundRobin's game rows.

import { useNavigate } from 'react-router-dom'
import VenueLink from '@/components/VenueLink'
import { useTeamDisplay, deriveTeamCode } from '@/utils/teamDisplay'
import { sf } from '../../../core/features/sf'
import { DivisionTag } from './DivisionPills'
import { useLabels } from '../../../core/labels'
import type { SchedulePageVM, ScheduleSideVM } from '../../../core/pages/schedule/types'

function TeamLogo({ side }: { side: ScheduleSideVM }) {
  if (side.logoUrl) return <img src={side.logoUrl} alt={side.name} className="w-8 h-8 object-contain rounded" />
  return (
    <div className="w-8 h-8 rounded bg-white/10 border border-secondary/30 flex items-center justify-center text-[10px] font-bold text-white/60">
      {deriveTeamCode(side.name)}
    </div>
  )
}

// `showDivision` (#583, "All divisions" only) tags each row with its division.
export default function SeasonGamesList({ seasonGames, withHeading, showDivision = false }: { seasonGames: SchedulePageVM['seasonGames']; withHeading: boolean; showDivision?: boolean }) {
  const t = useLabels()
  const teamName = useTeamDisplay()
  const navigate = useNavigate()
  // Keep showing the last list while a sport-stamped refetch is in flight.
  if (seasonGames.total === 0) return null
  return (
    <div className={withHeading ? 'mt-10' : ''} {...sf('schedule.season-games')}>
      {withHeading && (
        <h2 className="text-xl font-bold text-white mb-4">{t.term('regularSeason')}</h2>
      )}
      <div className="space-y-6">
        {seasonGames.days.map((day) => (
          <div key={day.key}>
            <h3 className="text-sm font-bold text-white/60 uppercase tracking-wider mb-3">{day.label ?? t.ui.common.tbd}</h3>
            <div className="space-y-3">
              {day.games.map((game) => (
                <div
                  key={game.id}
                  onClick={() => navigate(game.href)}
                  className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 rounded-lg overflow-hidden hover:border-primary/30 cursor-pointer transition-all p-4"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 flex items-center justify-end gap-3 min-w-0">
                      <span className="font-bold text-white truncate">{teamName({ name: game.home.name, short_code: game.home.shortCode }, 'auto')}</span>
                      <TeamLogo side={game.home} />
                    </div>
                    <div className="flex items-center gap-3 min-w-[100px] justify-center">
                      {game.final || game.live ? (
                        <>
                          <span className={`font-bold tabular-nums text-xl ${game.winner === 'home' ? 'text-green-400' : 'text-white'}`}>{game.home.score}</span>
                          <span className="text-white/60">-</span>
                          <span className={`font-bold tabular-nums text-xl ${game.winner === 'away' ? 'text-green-400' : 'text-white'}`}>{game.away.score}</span>
                        </>
                      ) : (
                        <span className="text-white/60 text-sm">{game.when.time}{game.when.tzAbbrev ? ` ${game.when.tzAbbrev}` : ''}</span>
                      )}
                    </div>
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <TeamLogo side={game.away} />
                      <span className="font-bold text-white truncate">{teamName({ name: game.away.name, short_code: game.away.shortCode }, 'auto')}</span>
                    </div>
                  </div>
                  <div className={showDivision ? 'mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm' : 'mt-3 flex items-center justify-center gap-4 text-sm'}>
                    {showDivision && game.division && <DivisionTag division={game.division} />}
                    <span className="text-white/60">{t.status({ status: game.status }, { variant: 'compact' }).text}</span>
                    {game.venue && (
                      <span className="text-white/60" onClick={(e) => e.stopPropagation()}>
                        <VenueLink venueId={game.venue.id} venueName={game.venue.name} parentVenueId={game.venue.parentId} parentVenueName={game.venue.parentName} />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
